import { AlertCircle, Inbox, RotateCw } from 'lucide-react';
import type { ReactNode } from 'react';

import { ApiClientError } from '@/lib/api';
import { cn } from '@/lib/cn';

/**
 * Loading / empty / error primitives.
 *
 * Every card on the funds page renders exactly one of four things: skeleton,
 * error, empty, or content. Keeping the three non-content states here means they
 * look and behave the same everywhere, and that no card can quietly forget one.
 */

// ─── Skeleton ────────────────────────────────────────────────────────────────

/**
 * A pulsing placeholder block. Give it the same box the real content will take
 * so nothing jumps when the query resolves.
 */
export function Skeleton({
  className,
  width,
  height,
  radius = 8,
}: {
  className?: string;
  width?: number | string;
  height?: number | string;
  radius?: number;
}) {
  return (
    <div
      aria-hidden="true"
      className={cn('skeleton-pulse', className)}
      style={{
        width,
        height,
        borderRadius: radius,
        background: 'linear-gradient(90deg, #EEF3F7 0%, #E3ECF3 50%, #EEF3F7 100%)',
      }}
    />
  );
}

/** Skeleton lines that stand in for a paragraph or a stack of table rows. */
export function SkeletonLines({
  rows = 3,
  height = 12,
  gap = 10,
  className,
}: {
  rows?: number;
  height?: number;
  gap?: number;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col', className)} style={{ gap }}>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} height={height} width={i === rows - 1 ? '62%' : '100%'} />
      ))}
    </div>
  );
}

/**
 * Announces to assistive tech that a region is loading, without spamming a
 * screen reader with every shimmering rectangle.
 */
export function LoadingRegion({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={className} role="status" aria-busy="true" aria-live="polite">
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}

// ─── Empty ───────────────────────────────────────────────────────────────────

/**
 * The calm "there is genuinely nothing here" state. Reached by, among others,
 * fund SFIS — a real fund with zero NAV rows.
 */
export function EmptyState({
  title = 'Belum ada data',
  message,
  compact = false,
}: {
  title?: string;
  message: string;
  compact?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center gap-2',
        compact ? 'px-4 py-8' : 'px-6 py-12',
      )}
    >
      <div
        className="flex items-center justify-center rounded-full"
        style={{ width: 40, height: 40, background: '#EFF6FB' }}
      >
        <Inbox style={{ width: 18, height: 18, color: '#8FA9BF' }} aria-hidden="true" />
      </div>
      <p
        className="text-sm font-semibold text-slate-600"
        style={{ fontFamily: 'var(--font-display)' }}
      >
        {title}
      </p>
      <p className="text-xs text-slate-400 max-w-[34ch] leading-relaxed">{message}</p>
    </div>
  );
}

// ─── Error ───────────────────────────────────────────────────────────────────

function describe(error: unknown): string {
  if (error instanceof ApiClientError) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return 'Terjadi kesalahan yang tidak diketahui.';
}

/** Inline failure with a retry button. Never a blank card, never a white screen. */
export function ErrorState({
  error,
  onRetry,
  compact = false,
}: {
  error: unknown;
  onRetry?: () => void;
  compact?: boolean;
}) {
  const code = error instanceof ApiClientError ? error.code : null;
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center text-center gap-2',
        compact ? 'px-4 py-8' : 'px-6 py-10',
      )}
    >
      <div
        className="flex items-center justify-center rounded-full"
        style={{ width: 40, height: 40, background: '#FEF2F2' }}
      >
        <AlertCircle style={{ width: 18, height: 18, color: '#DC2626' }} aria-hidden="true" />
      </div>
      <p
        className="text-sm font-semibold text-slate-700"
        style={{ fontFamily: 'var(--font-display)' }}
      >
        Gagal memuat data
      </p>
      <p className="text-xs text-slate-400 max-w-[40ch] leading-relaxed">{describe(error)}</p>
      {code ? (
        <p className="text-[10px] text-slate-300" style={{ fontFamily: 'var(--font-mono)' }}>
          {code}
        </p>
      ) : null}
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="mt-1 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-colors"
          style={{ background: 'rgba(234, 27, 26, 0.08)', color: '#EA1B1A' }}
        >
          <RotateCw style={{ width: 13, height: 13 }} aria-hidden="true" />
          Coba lagi
        </button>
      ) : null}
    </div>
  );
}
